import { Menu } from 'components/Menu/Menu'
import { Item } from '@react-stately/collections'

import Styles from './FilterOperators.module.scss'

import { usePropertyForCondition } from '../../../../hooks/usePropertyForCondition'
import { Button } from 'components/Button'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { PropertyType } from 'api/clients/PropertyClient'
import type { Condition } from '../../../../Filter'
import { Operator } from '../../../../constants'
import { ReactComponent as Chevron } from 'assets/icons/chevron-down__12.svg'
import { useHandbookReadOnlyUser } from 'hooks/useHandbookReadOnlyUser'
import { useFinderContext } from 'screens/Finder/FinderContext'

interface Props {
  condition: Condition
  onChangeOperator: (operator: Operator) => void
}

export const FilterOperators = ({ condition, onChangeOperator }: Props) => {
  const property = usePropertyForCondition(condition)
  const { isReadOnlyUser } = useHandbookReadOnlyUser()
  const { filesView } = useFinderContext()

  if (!property) return null

  const isCalendarProperty = !!(
    filesView?.schema.calendar_property_id && filesView.schema.calendar_property_id === property.id
  )

  const operatorTextForTextProperty = (operator: Operator) => {
    switch (operator) {
      case Operator.Contains:
        return 'contains'
      case Operator.NotContains:
        return 'does not contain'
      case Operator.Eq:
        return 'is'
      case Operator.NotEq:
        return 'is not'
    }
    return 'Unhandled'
  }

  const operatorTextForSingleSelectProperty = (operator: Operator) => {
    switch (operator) {
      case Operator.ContainsAny:
        return 'is any of'
      case Operator.NotContains:
        return 'is not any of'
      case Operator.Eq:
        return 'is'
      case Operator.NotEq:
        return 'is not'
    }
    return 'Unhandled'
  }

  const operatorTextForMultiSelectProperty = (operator: Operator) => {
    switch (operator) {
      case Operator.Contains:
        return 'has all of'
      case Operator.ContainsAny:
        return 'has any of'
      case Operator.NotContains:
        return 'has none of'
      case Operator.Eq:
        return 'is exactly'
    }
    return 'Unhandled'
  }

  const operatorTextForDateProperty = (operator: Operator) => {
    switch (operator) {
      case Operator.Eq:
        return 'is'
      case Operator.NotEq:
        return 'is not'
      case Operator.Between:
        return 'is between'
      case Operator.Lte:
        return 'is on or before'
      case Operator.Gte:
        return 'is on or after'
      case Operator.Gt:
        return 'is after'
      case Operator.Lt:
        return 'is before'
    }
    return 'Unhandled'
  }

  const operatorTextForDocumentProperty = (operator: Operator) => {
    // We should never get here right now

    switch (operator) {
      case Operator.Empty:
      case Operator.NotEmpty:
    }

    return 'Unhandled'
  }

  const operatorTextForPersonProperty = (operator: Operator) => {
    switch (operator) {
      case Operator.Contains:
        return 'has all of'
      case Operator.ContainsAny:
        return 'has any of'
      case Operator.NotContains:
        return 'has none of'
      case Operator.Eq:
        return 'is exactly'
    }
    return 'Unhandled'
  }

  const operatorTextForNumberProperty = (operator: Operator) => {
    switch (operator) {
      case Operator.Eq:
        return '='
      case Operator.NotEq:
        return '≠'
      case Operator.Lte:
        return '≤'
      case Operator.Gte:
        return '≥'
      case Operator.Gt:
        return '>'
      case Operator.Lt:
        return '<'
    }
    return 'Unhandled'
  }

  const operatorTextForLinkProperty = (operator: Operator) => {
    switch (operator) {
      case Operator.Contains:
        return 'contains'
      case Operator.NotContains:
        return 'does not contain'
      case Operator.Eq:
        return 'is'
      case Operator.NotEq:
        return 'is not'
    }
    return 'Unhandled'
  }

  const operatorTextForProperty = (operator: Operator) => {
    if (!property) return null

    if (operator === Operator.Empty) return 'is empty'
    if (operator === Operator.NotEmpty) return 'is not empty'

    switch (property.type) {
      case PropertyType.Title:
      case PropertyType.Text:
        return operatorTextForTextProperty(operator)
      case PropertyType.SingleSelect:
        return operatorTextForSingleSelectProperty(operator)
      case PropertyType.MultiSelect:
        return operatorTextForMultiSelectProperty(operator)
      case PropertyType.Date:
        return operatorTextForDateProperty(operator)
      case PropertyType.Document:
        return operatorTextForDocumentProperty(operator)
      case PropertyType.Person:
        return operatorTextForPersonProperty(operator)
      case PropertyType.Number:
        return operatorTextForNumberProperty(operator)
      case PropertyType.Link:
        return operatorTextForLinkProperty(operator)
    }
  }

  const operatorsForProperty = (propertyType: PropertyType) => {
    let defaultOperators = [Operator.Empty, Operator.NotEmpty]

    switch (propertyType) {
      case PropertyType.Title:
      case PropertyType.Text:
        return [Operator.Contains, Operator.NotContains, Operator.Eq, Operator.NotEq, ...defaultOperators]
      case PropertyType.SingleSelect:
        return [Operator.ContainsAny, Operator.NotContains, Operator.Eq, Operator.NotEq, ...defaultOperators]
      case PropertyType.MultiSelect:
        return [Operator.Contains, Operator.ContainsAny, Operator.NotContains, Operator.Eq, ...defaultOperators]
      case PropertyType.Date:
        return [
          Operator.Eq,
          Operator.NotEq,
          Operator.Between,
          Operator.Lte,
          Operator.Gte,
          Operator.Gt,
          Operator.Lt,
          ...defaultOperators,
        ]
      case PropertyType.Document:
        return [...defaultOperators]
      case PropertyType.Person:
        return [Operator.Contains, Operator.ContainsAny, Operator.NotContains, Operator.Eq, ...defaultOperators]
      case PropertyType.Number:
        return [Operator.Eq, Operator.NotEq, Operator.Lte, Operator.Gte, Operator.Gt, Operator.Lt, ...defaultOperators]
      case PropertyType.Link:
        return [Operator.Contains, Operator.NotContains, Operator.Eq, Operator.NotEq, ...defaultOperators]
    }

    return defaultOperators
  }

  if (property.type === PropertyType.Checkbox) {
    return (
      <Button className={Styles.operatorButton} variant="secondaryGrey" isDisabled={isReadOnlyUser}>
        <span>is</span>
        <Chevron className={Styles.chevron} />
      </Button>
    )
  }

  return (
    <DropdownTrigger popoverClassName={Styles.popover}>
      <Button
        className={Styles.operatorButton}
        variant="secondaryGrey"
        withChevron="down"
        isDisabled={isReadOnlyUser || isCalendarProperty}
      >
        {operatorTextForProperty(condition.operator)}
      </Button>
      {(close) => (
        <Menu
          selectionMode="single"
          onAction={(value) => {
            onChangeOperator(value as Operator)
            close()
          }}
          selectedKeys={new Set([condition.operator])}
          aria-label="Menu"
        >
          {property &&
            property.type &&
            operatorsForProperty(property.type).map((operator, index) => (
              <Item key={operator} textValue={operatorTextForProperty(operator) || 'Unavailable'}>
                {operatorTextForProperty(operator)}
              </Item>
            ))}
        </Menu>
      )}
    </DropdownTrigger>
  )
}
